/**
 * Requisitos de facturación del cliente en Reportes (Jun-2026, Fernando).
 * Separa «reporte aprobado» de «listo para facturar»: muestra el estado (Factura OK / Faltan datos / Conflicto),
 * qué falta exactamente y permite «Completar datos» in situ (mismo camino central PATCH /lineas/{id}/contratacion).
 * Fuente de datos: `eco.facturacion` (GET /rutas/{rid}/paradas/{idx}/economico → services/requisitos_facturacion).
 */
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { EditarContratacionDialog } from "@/components/ContratacionClienteBlock";
import { ShieldCheck, ShieldAlert, ShieldX, FilePenLine, Lock } from "lucide-react";

export const ESTADO_FACTURACION = {
  ok: { label: "Factura OK", corto: "Factura OK", dot: "🟢", cls: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40", Icon: ShieldCheck },
  faltan_datos: { label: "Faltan datos para facturar", corto: "Faltan datos", dot: "🟠", cls: "bg-amber-500/15 text-amber-200 border-amber-500/40", Icon: ShieldAlert },
  conflicto: { label: "Conflicto en datos de facturación", corto: "Conflicto", dot: "🔴", cls: "bg-rose-500/15 text-rose-200 border-rose-500/40", Icon: ShieldX },
};
const REQ_LABEL = { referencia_cliente: "Referencia", numero_contratacion: "Contratación", numero_pedido: "Pedido", importe_contratado_cliente: "Importe contratado" };
export const etiquetasFaltan = (faltan) => (faltan || []).map((f) => REQ_LABEL[f] || f);

/** Chip compacto para el listado izquierdo (solo clientes con requisitos). */
export function FacturacionChip({ fact, testId }) {
  if (!fact?.aplica) return null;
  const e = ESTADO_FACTURACION[fact.estado] || ESTADO_FACTURACION.faltan_datos;
  return (
    <span data-testid={testId} data-estado={fact.estado} title={fact.estado === "ok" ? "Requisitos de facturación del cliente completos" : `Falta: ${etiquetasFaltan(fact.faltan).join(", ") || "revisar conflicto"}`}
      className={`text-[9px] uppercase px-1.5 py-0.5 rounded border font-semibold ${e.cls}`}>
      {e.dot} {e.corto}
    </span>
  );
}

/** Bloque en el detalle: estado + faltantes + «Completar datos». `onSaved` → el padre recarga el económico. */
export function RequisitosFacturacionBlock({ fact, onSaved, open: openProp, onOpenChange }) {
  const [openLocal, setOpenLocal] = useState(false);
  const open = openProp ?? openLocal;
  const setOpen = onOpenChange ?? setOpenLocal;
  if (!fact?.aplica) return null;
  const e = ESTADO_FACTURACION[fact.estado] || ESTADO_FACTURACION.faltan_datos;
  const linea = fact.lineas?.[0];
  const ev = linea ? { ...linea.valores, conflictos: linea.conflictos } : {};
  const faltan = etiquetasFaltan(fact.faltan);
  return (
    <div className={`border rounded-lg p-3 space-y-2 ${e.cls}`} data-testid="requisitos-facturacion" data-estado={fact.estado}>
      <div className="flex items-center gap-2 flex-wrap">
        <e.Icon className="w-4 h-4 shrink-0" />
        <span className="text-xs font-bold" data-testid="requisitos-facturacion-estado">{e.label}</span>
        <span className="text-[10px] opacity-80">· {fact.cliente?.nombre} exige: {fact.requisitos.map((r) => REQ_LABEL[r] || r).join(", ")}</span>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="completar-datos-btn"
          className="ml-auto h-7 text-[11px] bg-zinc-950/60 border-current/40 hover:bg-zinc-900 text-inherit">
          <FilePenLine className="w-3.5 h-3.5 mr-1" /> {fact.estado === "ok" ? "Ver / editar datos" : "Completar datos"}
        </Button>
      </div>
      {fact.estado === "faltan_datos" && (
        <div className="text-[11px] flex items-center gap-1.5 flex-wrap" data-testid="requisitos-facturacion-faltan">
          <span className="opacity-80">Falta:</span>
          {faltan.map((f) => <span key={f} className="px-1.5 py-0.5 rounded bg-zinc-950/50 border border-current/30 font-semibold">{f}</span>)}
        </div>
      )}
      {fact.estado === "conflicto" && (
        <div className="text-[11px]" data-testid="requisitos-facturacion-conflicto">
          Hay valores distintos registrados para la misma línea (correo vs manual). Revisa el conflicto en «Completar datos»: no se sobrescribe nada automáticamente.
        </div>
      )}
      {linea && (
        <div className="text-[10px] font-mono opacity-90 flex gap-3 flex-wrap" data-testid="requisitos-facturacion-valores">
          <span>Ref. {linea.valores.referencia_cliente || "—"}</span>
          <span>Contratación {linea.valores.numero_contratacion || "—"}</span>
          <span>Pedido {linea.valores.numero_pedido || "—"}</span>
          {(fact.requisitos.includes("importe_contratado_cliente") || linea.valores.importe_contratado_cliente != null) && <span>Importe {linea.valores.importe_contratado_cliente != null ? `${Number(linea.valores.importe_contratado_cliente).toFixed(2).replace(".", ",")} €` : "—"}</span>}
        </div>
      )}
      {open && linea && (
        <EditarContratacionDialog open={open} onOpenChange={setOpen} lineaId={linea.linea_id} ev={ev} requisitos={fact.requisitos} origen="reportes"
          onSaved={() => { onSaved?.(); }} />
      )}
    </div>
  );
}

/** Sustituto del botón «Enviar a FD» cuando el cliente tiene requisitos incompletos. */
export function BotonFDBloqueado({ fact, onCompletar }) {
  const e = ESTADO_FACTURACION[fact.estado] || ESTADO_FACTURACION.faltan_datos;
  return (
    <div className={`rounded-md border p-2 text-[11px] flex flex-col gap-1 ${e.cls}`} data-testid="enviar-fd-bloqueado" data-estado={fact.estado}
      title="El envío a FacturaDirecta está bloqueado hasta completar los requisitos del cliente (también en el servidor).">
      <div className="flex items-center gap-1.5 font-bold"><Lock className="w-3.5 h-3.5" /> {fact.estado === "conflicto" ? "FD bloqueado · conflicto de datos" : "Faltan datos para facturar"}</div>
      {fact.estado === "faltan_datos" && <div className="opacity-90">Falta: {etiquetasFaltan(fact.faltan).join(", ")}</div>}
      <button type="button" onClick={onCompletar} data-testid="enviar-fd-bloqueado-completar" className="underline text-left font-semibold hover:opacity-80">Completar datos →</button>
    </div>
  );
}
